import React, { useMemo, useState } from 'react';
import { NavLink, Redirect, Route, Switch } from 'react-router-dom';

import { Skills, SkillsV1 } from './skills';
import AuthorizedRoute from '../AuthorizedRoute';
import UserPermissions from './users/userPermissions';
import Designation from './designation/Designation';
import AddEditDesignation from './designation/AddEditDesignation';

import { en } from 'constants/lang/en';
import * as permissions from 'constants/permissions';
import {
  CREATE_DESIGNATION,
  DESIGNATION,
  SKILLS,
  SKILLS_V1,
  UPDATE_DESIGNATION,
  USER_PERMISSION,
} from 'constants/routes';

import { getDefaultPath } from 'utils/path';

import useModalState from 'hooks/useModalState';
import { useUserPermissions } from 'hooks/query/useUserInfo';

type PermissionKey = string;
type PermissionMap = Record<PermissionKey, boolean>;

type ModalState = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onClose: () => void;
};

export interface AuthUser {
  id: number | string;
}

interface SettingsProps {
  user: AuthUser;
}

interface NavBarItem {
  label: string;
  path: string;
  permission: PermissionKey;
}

type NavBarItemWithPermission = NavBarItem & { hasPermission: boolean };

const SETTINGS_NAV_ITEMS: ReadonlyArray<NavBarItem> = [
  // {
  //   label: 'Skills V1',
  //   path: SKILLS_V1,
  //   permission: permissions.SKILLS.VIEW,
  // },
  {
    label: 'Skills',
    path: SKILLS,
    permission: permissions.SKILLS.VIEW,
  },
  {
    label: 'Designation',
    path: DESIGNATION,
    permission: permissions.DESIGNATIONS.VIEW,
  },
  {
    label: 'Role Management',
    path: USER_PERMISSION,
    permission: permissions.USER_PERMISSIONS.VIEW,
  },
];

interface SettingsActionBarProps {
  navBars: ReadonlyArray<NavBarItemWithPermission>;
  canManageSkills: boolean;
  onOpenGlobalRatingModal: () => void;
  onOpenCategoryModal: () => void;
}

function SettingsActionBar({
  navBars,
  canManageSkills,
  onOpenGlobalRatingModal,
  onOpenCategoryModal,
}: SettingsActionBarProps) {
  return (
    <div className="mtpx-30 f-wrap justify-content-between d-flex nav-card">
      <ul className="secondary-nav__wrap">
        {navBars.map(({ label, path, hasPermission }) => {
          if (!hasPermission) {
            return null;
          }

          return (
            <li key={path} className="secondary-nav__node">
              <NavLink
                className="secondary-nav__link secondary-nav__link--primary clear-after"
                activeClassName="active"
                to={path}
              >
                {label}
              </NavLink>
            </li>
          );
        })}
      </ul>

      <Switch>
        {canManageSkills && (
          <Route
            exact
            path={SKILLS}
            render={() => (
              <div>
                <button className="button button--primary" onClick={onOpenGlobalRatingModal}>
                  {en.BUTTON.EDIT_GLOBAL_RATING}
                </button>

                <button className="button button--secondary" onClick={onOpenCategoryModal}>
                  {en.BUTTON.ADD_CATEGORY}
                </button>
              </div>
            )}
          />
        )}
      </Switch>
    </div>
  );
}

function Settings({ user }: SettingsProps) {
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const userPermissions = useUserPermissions() as PermissionMap;

  const {
    open: openGlobalRatingModal,
    setOpen: setOpenGlobalRatingModal,
    onClose: onGlobalRatingModalClose,
  } = useModalState() as ModalState;

  const navBarsWithPermissions: ReadonlyArray<NavBarItemWithPermission> = useMemo(() => {
    return SETTINGS_NAV_ITEMS.map((navBar) => {
      return {
        ...navBar,
        hasPermission: Boolean(userPermissions[navBar.permission]),
      };
    });
  }, [userPermissions]);

  const customClassName = 'plpx-0 prpx-0' as const;

  const canManageSkills = Boolean(userPermissions[permissions.SKILLS.CREATE]);

  const defaultPath =
    (getDefaultPath(navBarsWithPermissions) as string | undefined) ??
    navBarsWithPermissions.find((navBar) => navBar.hasPermission)?.path ??
    SKILLS;

  return (
    <main>
      <div className="container">
        <SettingsActionBar
          navBars={navBarsWithPermissions}
          canManageSkills={canManageSkills}
          onOpenGlobalRatingModal={() => setOpenGlobalRatingModal(true)}
          onOpenCategoryModal={() => setIsCategoryModalOpen(true)}
        />

        <Switch>
          <AuthorizedRoute
            permission={[permissions.SKILLS.VIEW]}
            allowSelf={false}
            forbiddenRoles={undefined}
            exact
            path={SKILLS_V1}
            component={SkillsV1}
            user={user}
            customClassName={customClassName}
          />

          <AuthorizedRoute
            permission={[permissions.SKILLS.VIEW]}
            allowSelf={false}
            forbiddenRoles={undefined}
            exact
            path={SKILLS}
            component={Skills}
            openGlobalRatingModal={openGlobalRatingModal}
            onGlobalRatingModalClose={onGlobalRatingModalClose}
            isCategoryModalOpen={isCategoryModalOpen}
            setIsCategoryModalOpen={setIsCategoryModalOpen}
            user={user}
            customClassName={customClassName}
          />

          <AuthorizedRoute
            permission={[permissions.DESIGNATIONS.VIEW]}
            allowSelf={false}
            forbiddenRoles={undefined}
            exact
            path={DESIGNATION}
            component={Designation}
            customClassName={customClassName}
          />
          <AuthorizedRoute
            permission={[permissions.DESIGNATIONS.CREATE]}
            allowSelf={false}
            forbiddenRoles={undefined}
            path={CREATE_DESIGNATION}
            component={AddEditDesignation}
            customClassName={customClassName}
          />
          <AuthorizedRoute
            exact
            permission={[permissions.DESIGNATIONS.UPDATE]}
            allowSelf={false}
            forbiddenRoles={undefined}
            path={UPDATE_DESIGNATION}
            component={AddEditDesignation}
            customClassName={customClassName}
          />

          <AuthorizedRoute
            permission={[permissions.USER_PERMISSIONS.VIEW]}
            allowSelf={false}
            forbiddenRoles={undefined}
            exact
            path={USER_PERMISSION}
            component={UserPermissions}
            customClassName={customClassName}
          />

          <Redirect to={defaultPath} />
        </Switch>
      </div>
    </main>
  );
}

export default Settings;
